import React, { useEffect, useState } from "react";
import { useStaticQuery, graphql } from "gatsby";
import Container from "./container";
import { cn, handleize } from "../lib/helpers";
import LinkElem from "./link";
import Button from "./button";
import PortableText from "./portableText";
import FooterMenuGroup from "../components/footer-menu-group";
import Svg from 'react-inlinesvg';
import Icon from "./icon";
import { useCookies } from 'react-cookie';

import * as styles from "../styles/components/footer.module.css";

interface LinkNode {
  _key?: string;
  url?: string;
  blank?: boolean;
  text?: string;
}

interface Newsletter {
  title?: string;
  _rawCopy?: any[];
  button?: LinkNode;
}

interface SiteSettings {
  title?: string;
  newsletter?: Newsletter;
}

interface FooterMenuItem {
  _key?: string;
  title?: string;
  menu?: LinkNode[];
}

interface FooterQueryData {
  site?: SiteSettings;
  nav?: {
    footerMenu?: FooterMenuItem[];
    socialMedia?: LinkNode[];
    bottomLinks?: LinkNode[];
  };
  contact?: {
    _rawAddress?: any[];
    phoneTf?: LinkNode;
    phone?: LinkNode;
    email?: LinkNode;
  }
}

const Footer: React.FC = () => {
  const data = useStaticQuery<FooterQueryData>(graphql`
    query FooterQuery {
      site: sanitySettings {
        title
        newsletter {
          title
          _rawCopy
          button {
            blank
            text
            url
          }
        }
        cookiesInfo {
          _rawCopy
          buttonText
        }
      }

      nav: sanityNav {
        footerMenu {
          _key
          title
          menu {
            _key
            blank
            text
            url
          }
        }
        socialMedia {
          _key
          text
          url
        }
        bottomLinks {
          _key
          blank
          text
          url
        }
      }

      contact: sanityContact {
        _rawAddress
        phoneTf {
          text
          url
        }
        phone {
          text
          url
        }
        email {
          text
          url
        }
      }
    }
  `);

  // Destructure your query data for easier access
  const { site, nav, contact } = data || {};
  const { title, newsletter, cookiesInfo } = site || {};
  const { footerMenu, socialMedia, bottomLinks } = nav || {};
  const { _rawAddress, phoneTf, phone, email } = contact || {};

  const [hideCookies, setHideCookies] = useState(true);
  const [cookies, setCookie] = useCookies(['hideCookiesMsg']);

  const handleHideCookies = () => {
    setCookie('hideCookiesMsg', true, {maxAge: 604800});
    setHideCookies(true);
  }

  const handleSetHideCookies = () => {
    if(cookies.hideCookiesMsg === undefined) {
      setHideCookies(false);
    }
  }

  useEffect(() => {
    handleSetHideCookies();
  }, []);


  return(
    <footer className={styles.root}>
      <Container>
        <div className={styles.top}>
          <div className="grid">
            <div className="col-lg-4">
              {newsletter && (
                <div className={styles.newsletter}>
                  {newsletter.title && (
                    <div className={cn("body-small", styles.heading)}>{newsletter.title}</div>
                  )}
                  {newsletter._rawCopy && (
                    <div className={cn("entry-content white h4", styles.copy)}><PortableText blocks={newsletter._rawCopy} theme="white" /></div>
                  )}
                  <Button node={newsletter.button} theme="white" size="small" />
                </div>
              )}
            </div>


            <div className="col-lg-7 offset-lg-1">
              <div className={cn("grid", styles.grid)}>
                <div className={cn("col-lg-7", styles.col)}>
                  {footerMenu?.length > 0 && (
                    <nav className={styles.footerMenuBlock}>
                      <ul className={cn("grid", styles.footerMenu)}>
                        {footerMenu.map(({ _key, title, menu }) => (
                          <FooterMenuGroup key={_key} title={title} menu={menu} />
                        ))}
                      </ul>
                    </nav>
                  )}
                </div>
                <div className={cn("col-lg-5", styles.col)}>
                  {(_rawAddress || email?.url || phoneTf?.url || phone?.url) && (
                    <div className={styles.contact}>
                      <div className={cn("body-small", styles.heading)}>Head Office</div>

                      {_rawAddress && (
                        <div className={cn("entry-content", styles.row)}><PortableText blocks={_rawAddress} /></div>
                      )}

                      {email?.url && (
                        <div className={styles.row}><LinkElem node={email} contact={true} noStyle={true} /></div>
                      )}
                      {(phoneTf?.url || phone?.url) && (
                        <div className={styles.row}>
                          {phoneTf?.url && (
                            <div>Toll-Free: <LinkElem node={phoneTf} contact={true} noStyle={true} /></div>
                          )}

                          {phone?.url && (
                            <div><LinkElem node={phone} contact={true} noStyle={true} /></div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {socialMedia?.length > 0 && (
                    <nav className={styles.socialMediaBlock}>
                      <ul className={styles.socialMedia}>
                        {socialMedia.map(({ _key, text, url }) => (
                          <li key={_key}>
                            <a href={url} target="_blank" rel="noreferrer">
                              <span className="sr-only">{text}</span>
                              <Icon symbol={handleize(text)} />
                            </a>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.bottom}>
          <div className={styles.copyright}>
            &copy; {new Date().getFullYear()} {title}
          </div>

          {bottomLinks?.length > 0 && (
            <nav className={styles.bottomLinksBlock}>
              <ul className={styles.bottomLinks}>
                {bottomLinks.map((node) => (
                  <li key={node._key}>
                    <LinkElem node={node} noStyle={true} />
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      </Container>

      {cookiesInfo?._rawCopy && (
        <div className={cn(styles.cookies, !hideCookies && styles.active)}>
          <div className={cn("entry-content body-small", styles.copy)}>
            <PortableText blocks={cookiesInfo._rawCopy} />
          </div>
          <button onClick={handleHideCookies}>{cookiesInfo?.buttonText || "I Accept Cookies"}</button>
        </div>
      )}

    </footer>
  );
};

export default Footer;
